import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AppSetting {
  id: string;
  key: string;
  value: any;
  type: 'boolean' | 'string' | 'number' | 'json' | 'date';
  category: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface UseAppSettingsReturn {
  settings: AppSetting[];
  loading: boolean;
  error: string | null;
  getSetting: (key: string) => any;
  updateSetting: (key: string, value: any) => Promise<void>;
  getSettingsByCategory: (category: string) => AppSetting[];
  refetch: () => Promise<void>;
}

export function useAppSettings(): UseAppSettingsReturn {
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('app_settings')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;

      // Parsear valores JSON
      const parsedSettings = data?.map(setting => ({
        ...setting,
        type: setting.type as 'boolean' | 'string' | 'number' | 'json' | 'date',
        description: setting.description || undefined,
        value: parseSettingValue(setting.value, setting.type)
      })) || [];
      setSettings(parsedSettings);
    } catch (err: any) {
      setError(err.message);
      
    } finally {
      setLoading(false);
    }
  };

  const parseSettingValue = (value: any, type: string) => {
    if (value === null || value === undefined) return null;
    
    // Si el valor ya está parseado correctamente para el tipo esperado, devolverlo
    if (type === 'number' && typeof value === 'number') {
      return value;
    }
    
    if (type === 'boolean' && typeof value === 'boolean') {
      return value;
    }
    
    // Si no es string, intentar parsearlo como JSON
    if (typeof value !== 'string') {
      return value;
    }
    
    try {
      // Intentar parsear como JSON primero
      const parsed = JSON.parse(value);
      
      switch (type) {
        case 'boolean':
          // Manejo robusto de booleanos
          if (typeof parsed === 'boolean') {
            return parsed;
          }
          if (typeof parsed === 'string') {
            return parsed.toLowerCase() === 'true';
          }
          return Boolean(parsed);
        case 'number':
          return Number(parsed);
        case 'string':
          return String(parsed);
        case 'date':
          return String(parsed);
        case 'json':
          return parsed;
        default:
          return parsed;
      }
    } catch {
      // Si no es JSON válido, procesar según el tipo
      switch (type) {
        case 'number':
          return Number(value) || 0;
        case 'boolean':
          // Manejo directo de strings para booleanos
          const lowerValue = value.toLowerCase();
          return lowerValue === 'true' || lowerValue === '1' || lowerValue === 'yes';
        case 'string':
          return String(value);
        case 'date':
          return String(value);
        default:
          return value;
      }
    }
  };

  const getSetting = (key: string): any => {
    const setting = settings.find(s => s.key === key);
    return setting?.value ?? null;
  };

  const getSettingsByCategory = (category: string): AppSetting[] => {
    return settings.filter(s => s.category === category);
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      // Serializar el valor para almacenarlo en JSONB
      const serializedValue = JSON.stringify(value);

      const { data, error: updateError } = await supabase
        .from('app_settings')
        .update({ 
          value: serializedValue,
          updated_at: new Date().toISOString()
        })
        .eq('key', key)
        .select();

      if (updateError) {
        
        throw updateError;
      }

      if (!data || data.length === 0) {
        throw new Error(`No se pudo actualizar la configuración ${key}`);
      }

      toast({
        title: "Configuración actualizada",
        description: `La configuración ha sido actualizada correctamente.`,
      });

    } catch (err: any) {
      
      toast({
        title: "Error al actualizar configuración",
        description: err.message,
        variant: "destructive",
      });
      throw err;
    }
  };

  const refetch = async () => {
    await fetchSettings();
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return {
    settings,
    loading,
    error,
    getSetting,
    updateSetting,
    getSettingsByCategory,
    refetch,
  };
}

// Hook específico para configuraciones de inscripción
export function useEnrollmentSettings() {
  const { getSetting, loading, refetch } = useAppSettings();
  
  const enrollmentSettings = {
    isEnrollmentEnabled: getSetting('parent_enrollment_enabled'),
    enrollmentStartDate: getSetting('enrollment_start_date'),
    enrollmentEndDate: getSetting('enrollment_end_date'),
    enrollmentMessage: getSetting('enrollment_message') ?? '',
    loading,
  };

  return {
    ...enrollmentSettings,
    refetch,
  };
}